import { HUMAN_REVIEW_OUTCOME, HUMAN_REVIEW_STATUS } from "@/ai-coding-outputs/constants";
import { HttpStatusError, QA_API_DOMAIN, fetchJsonWithSupabaseAuth, fetchWithSupabaseAuth } from "@/api/classifier";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { toast } from "sonner";
import type { EncounterHumanCodingReviewData } from "./useHumanCodingReviewData";

const MARK_ENCOUNTER_REVIEWED_URL = `${QA_API_DOMAIN}/reviews/mark-encounter-reviewed`;
const MARK_ENCOUNTER_REJECTED_URL = `${QA_API_DOMAIN}/reviews/mark-encounter-rejected`;
const REPLACE_REVIEW_CODES_URL = `${QA_API_DOMAIN}/reviews/replace-review-codes`;

type FinalizeCodesData = {
  encounterId: string;
  procedures: {
    opcs_4_code: string;
    description: string;
    order_index: number;
  }[];
  diagnoses: {
    icd_10_code: string;
    description: string;
    order_index: number;
  }[];
};

type ReviewCodeResponse = {
  code: string;
  description: string | null;
  order_index: number;
};

type ReplaceReviewCodesResponse = {
  review_id: string;
  status: EncounterHumanCodingReviewData["reviewStatus"];
  outcome: EncounterHumanCodingReviewData["reviewOutcome"];
  diagnoses: ReviewCodeResponse[];
  procedures: ReviewCodeResponse[];
};

const replaceReviewCodes = (body: { encounter_id: string; diagnoses?: string[]; procedures?: string[] }) =>
  fetchJsonWithSupabaseAuth<ReplaceReviewCodesResponse>(REPLACE_REVIEW_CODES_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

type CachedReviewState = Pick<EncounterHumanCodingReviewData, "reviewStatus" | "reviewOutcome">;

// A save can have activated the database's revert trigger, so a state we could not read must stop
// claiming approval: a needless re-approval costs a coder a minute, a false one hides the encounter.
const withdrawnApproval = (cached: EncounterHumanCodingReviewData): CachedReviewState | undefined =>
  cached.reviewStatus === HUMAN_REVIEW_STATUS.COMPLETE && cached.reviewOutcome === HUMAN_REVIEW_OUTCOME.APPROVED
    ? { reviewStatus: HUMAN_REVIEW_STATUS.PENDING, reviewOutcome: cached.reviewOutcome }
    : undefined;

type DoNotOutputData = {
  encounterId: string;
  reason: string;
  notes?: string;
};

export const useEncounterHumanReviewMutations = () => {
  const queryClient = useQueryClient();

  // A prefix filter: the review query's key carries a classification-result id this hook does not
  // know, so an exact two-segment write would land on an entry nothing reads.
  const applyCachedReviewState = useCallback(
    (encounterId: string, nextState: (cached: EncounterHumanCodingReviewData) => CachedReviewState | undefined) => {
      queryClient.setQueriesData<EncounterHumanCodingReviewData>(
        { queryKey: ["encounter-human-coding-review", encounterId] },
        (previous) => {
          if (!previous) return previous;

          const state = nextState(previous);
          if (!state) return previous;
          if (previous.reviewStatus === state.reviewStatus && previous.reviewOutcome === state.reviewOutcome) {
            return previous;
          }

          return { ...previous, ...state };
        },
      );
    },
    [queryClient],
  );

  const ensureReviewExistsMutation = useMutation({
    mutationFn: ({ encounterId }: { encounterId: string }) => replaceReviewCodes({ encounter_id: encounterId }),
  });

  const saveCodesMutation = useMutation({
    mutationFn: (data: FinalizeCodesData) =>
      replaceReviewCodes({
        encounter_id: data.encounterId,
        diagnoses: data.diagnoses.map((d) => d.icd_10_code),
        procedures: data.procedures.map((p) => p.opcs_4_code),
      }),
    // The save can itself have activated the database's revert trigger, and the response reports the
    // status it left behind — so take it, rather than re-reading and discarding unsaved edits.
    onSuccess: (replaced, variables) =>
      applyCachedReviewState(variables.encounterId, () => ({
        reviewStatus: replaced.status,
        reviewOutcome: replaced.outcome,
      })),
    onError: (error, variables) => {
      console.error("Save codes error:", error);
      // A 409 names the review's true state — voided, or rejected — which `pending` would contradict.
      if (error instanceof HttpStatusError && error.status === 409) return;

      applyCachedReviewState(variables.encounterId, withdrawnApproval);
    },
  });

  const finalizeMutation = useMutation({
    mutationFn: async (data: FinalizeCodesData) => {
      const body = {
        encounter_id: data.encounterId,
        diagnoses: data.diagnoses.map((d) => d.icd_10_code),
        procedures: data.procedures.map((p) => p.opcs_4_code),
      };

      const res = await fetchWithSupabaseAuth(MARK_ENCOUNTER_REVIEWED_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${res.status}: Failed to mark encounter reviewed`);
      }

      return res.json() as Promise<{
        ok: boolean;
        human_review_id: string;
        hrg_id: string | null;
      }>;
    },
    onSuccess: () => {
      toast.success("Clinical codes have been saved and finalized for encounter.");
      queryClient.invalidateQueries({
        queryKey: ["encounter-human-coding-review"],
      });
      queryClient.invalidateQueries({
        queryKey: ["next-unreviewed-encounter"],
      });
    },
    onError: (error) => {
      console.error("Finalize error:", error);
      toast.error("Failed to finalize codes. Please try again.");
    },
  });

  const rejectOutputMutation = useMutation({
    mutationFn: async (data: DoNotOutputData) => {
      const notes = `Reason: ${data.reason}${data.notes ? `\nNotes: ${data.notes}` : ""}`;

      const res = await fetchWithSupabaseAuth(MARK_ENCOUNTER_REJECTED_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          encounter_id: data.encounterId,
          notes,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${res.status}: Failed to mark encounter rejected`);
      }

      return res.json() as Promise<{
        ok: boolean;
        human_review_id: string;
        hrg_id: string | null;
      }>;
    },
    onSuccess: () => {
      toast.success("Encounter marked as do not output codes.");
      queryClient.invalidateQueries({
        queryKey: ["encounter-human-coding-review"],
      });
      queryClient.invalidateQueries({
        queryKey: ["next-unreviewed-encounter"],
      });
    },
    onError: (error) => {
      console.error("Do not output error:", error);
      toast.error("Failed to save review decision. Please try again.");
    },
  });

  return {
    ensureReviewExistsMutation,
    saveCodes: saveCodesMutation,
    finalizeCodes: finalizeMutation,
    rejectOutput: rejectOutputMutation,
    isFinalizingCodes: finalizeMutation.isPending,
    isProcessingDoNotOutput: rejectOutputMutation.isPending,
  };
};
